import type { MeasureMetadata } from '../../api/work-periods-client/work-periods-client.type';

/**
 * Get the unique identifier for a measure metadata object
 * @param {MeasureMetadata} measureMetadata - The measure metadata object to get the id for
 * @returns {string} The unique identifier for the measure metadata object
 */
const getMeasureMetadataId = (measureMetadata: MeasureMetadata): string => {
  return measureMetadata.config_id
    ? `${measureMetadata.measure_name}-${measureMetadata.config_id}`
    : measureMetadata.measure_name;
};

/**
 * Table data structure for calculations export
 */
type TableSection = {
  title: string;
  measures: MeasureMetadata[];
};

/**
 * Downloads calculations data as a CSV file grouped by sections
 * @param {TableSection[]} tableData - Array of table sections with their measures
 * @param {MeasureMetadata[]} sprintMeasures - Array of sprint measures to identify sprint columns
 */
const downloadCalculationsCSV = (tableData: TableSection[], sprintMeasures: MeasureMetadata[] = []): void => {
  try {
    const csvRows: string[] = [];

    // grammar-check disable-next-line COMMA_PARENTHESIS_WHITESPACE
    csvRows.push('Group,Name,Description,Purpose,Units,Sprint Measure');

    const sprintIds = new Set(sprintMeasures.map(getMeasureMetadataId));

    // Utility to escape CSV once, not per row
    const escapeCSV = (value: string | null | undefined): string => {
      if (value == null) return '';
      const escaped = String(value).replace(/"/g, '""');
      return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
    };

    // Process each section
    tableData.forEach((section) => {
      section.measures.forEach((measure) => {
        const isSprintMeasure = sprintIds.has(getMeasureMetadataId(measure));

        const row = [
          escapeCSV(section.title),
          escapeCSV(measure.measure_title),
          escapeCSV(measure.measure_description),
          escapeCSV(measure.measure_purpose),
          escapeCSV(measure.measure_units),
          isSprintMeasure ? 'Yes' : 'No',
        ].join(',');

        csvRows.push(row);
      });
    });

    // Create and download the file
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `calculations-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error('Error downloading CSV:', error);
    // You could add a toast notification here if you have a notification system
  }
};

export { downloadCalculationsCSV, getMeasureMetadataId };
export type { TableSection };
